"use client";

import { ControlledTextField, Button } from "../../../../shared";
import {
    Box,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Typography,
    useColorScheme,
} from "@mui/joy";
import { useState, useEffect } from "react";
import {
    resolveAccentBackground,
    resolveAccentForeground,
} from "../../../../utils/accentColors";

interface ColorFormProps {
    color?: any;
    onSubmit: (data: any) => void | Promise<void>;
    onClose: () => void;
}

export function ColorForm({ color, onSubmit, onClose }: ColorFormProps) {
    const { mode } = useColorScheme();
    const [formData, setFormData] = useState({
        Name: "",
        LightHex: "",
        DarkHex: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (color) {
            setFormData({
                Name: color.Name || "",
                LightHex: color.LightHex || "",
                DarkHex: color.DarkHex || "",
            });
        }
    }, [color]);

    const preview = {
        Name: formData.Name || "Preview",
        LightHex: formData.LightHex || "#e5e7eb",
        DarkHex: formData.DarkHex || "#374151",
    };
    const activeBg = resolveAccentBackground(preview, mode);
    const activeFg = resolveAccentForeground(preview, mode);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const submitData = color ? { ...formData, Id: color.Id } : formData;
            await onSubmit(submitData);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={true} onClose={onClose}>
            <ModalDialog size="md">
                <ModalClose />
                <DialogTitle>{color ? "Edit Color" : "Add Color"}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Stack spacing={2}>
                            <ControlledTextField
                                label="Name"
                                helperText="Stored on calendar events (e.g. Purple, Blue)"
                                value={formData.Name}
                                onChange={(Name) =>
                                    setFormData({ ...formData, Name })
                                }
                                required
                            />
                            <ControlledTextField
                                label="Light theme hex"
                                helperText="Used as the event background in light mode"
                                value={formData.LightHex}
                                onChange={(LightHex) =>
                                    setFormData({ ...formData, LightHex })
                                }
                                placeholder="#bfdbfe"
                                required
                            />
                            <ControlledTextField
                                label="Dark theme hex"
                                helperText="Used as the event background in dark mode"
                                value={formData.DarkHex}
                                onChange={(DarkHex) =>
                                    setFormData({ ...formData, DarkHex })
                                }
                                placeholder="#1e3a8a"
                                required
                            />

                            <Box>
                                <Typography
                                    level="body-xs"
                                    sx={{ color: "text.secondary", mb: 0.75 }}
                                >
                                    Preview in {mode === "dark" ? "dark" : "light"}{" "}
                                    theme
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        bgcolor: activeBg,
                                        color: activeFg,
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: "4px",
                                        fontWeight: 500,
                                        fontSize: "13px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 3,
                                            alignSelf: "stretch",
                                            bgcolor: activeFg,
                                            borderRadius: 1,
                                        }}
                                    />
                                    Sample calendar event
                                </Box>
                                <Box display="flex" gap={1} mt={1}>
                                    <Box flex={1}>
                                        <Typography level="body-xs" mb={0.25}>
                                            Light
                                        </Typography>
                                        <Box
                                            height={28}
                                            borderRadius="4px"
                                            sx={{
                                                bgcolor: preview.LightHex,
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <Typography level="body-xs" mb={0.25}>
                                            Dark
                                        </Typography>
                                        <Box
                                            height={28}
                                            borderRadius="4px"
                                            sx={{
                                                bgcolor: preview.DarkHex,
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" loading={submitting}>
                            {color ? "Update" : "Create"}
                        </Button>
                    </DialogActions>
                </form>
            </ModalDialog>
        </Modal>
    );
}
